import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { NotFoundPage } from '@apple/ui/shell/pages/NotFoundPage';
import { NotAuthorizedError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { OrderDetails } from '@apple/features/order-retail/components/OrderDetails/OrderDetails';
import { orderQueries } from '@apple/features/order-retail/queries/order';

export const Route = createFileRoute('/_authed/orders/$poNumber')({
	errorComponent: ({ error }) => {
		if (error instanceof NotAuthorizedError) {
			return <NotFoundPage />;
		} else {
			throw error;
		}
	},
	parseParams: ({ poNumber }) => ({
		poNumber: z.string().parse(poNumber),
	}),
	wrapInSuspense: true,
	component: OrderRoute,
	validateSearch: z.object({
		activeTab: z.enum(['order-details', 'order-comments']).optional(),
	}),
	beforeLoad: args => {
		requireAuth(args);
	},
});

function OrderRoute() {
	const goBackToList = useBackOrNavigate();
	const { t } = useTranslation('order');
	const { poNumber } = Route.useParams();
	const [orderQuery, orderLinesQuery, orderCommentsQuery] = useSuspenseQueries({
		queries: [
			orderQueries.order(poNumber),
			orderQueries.orderLines(poNumber),
			orderQueries.orderComments(poNumber),
		],
	});
	const order = orderQuery.data;
	const search = Route.useSearch();
	const activeTab = search.activeTab ?? 'order-details';

	return (
		<DetailsLayout
			title={t('orderDetails.title', {
				orderNumber: order.purchaseOrderNumber,
			})}
		>
			<OrderDetails
				activeTab={activeTab}
				order={orderQuery.data}
				lines={orderLinesQuery.data}
				comments={orderCommentsQuery.data}
				onOrderSaved={() => void goBackToList('/orders/search')}
			/>
		</DetailsLayout>
	);
}
